import React, { useState, useEffect } from "react";
import { Container, Box, Paper, Typography, Button, TextField, CircularProgress, Alert, Divider, Chip } from "@mui/material";
import { ShieldCheck, ShieldOff, KeyRound, CheckCircle } from "lucide-react";
import TitleBox from "../Shared/TitleBox";
import * as api from "../../api/index";
import { useTranslation } from "react-i18next";
const STEP = {
  IDLE: "idle",
  SCAN: "scan",
  DISABLE: "disable"
};

// ── Reusable content (used both on the page and inside the modal) ─────────────
export function MFAContent() {
  const {
    t
  } = useTranslation("admin");
  const [mfaEnabled, setMfaEnabled] = useState(null);
  const [step, setStep] = useState(STEP.IDLE);
  const [qrCode, setQrCode] = useState("");
  const [secret, setSecret] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  useEffect(() => {
    api.mfaStatus().then(r => setMfaEnabled(r.data?.mfaEnabled)).catch(() => {});
  }, []);
  const reset = () => {
    setStep(STEP.IDLE);
    setCode("");
    setError("");
    setQrCode("");
    setSecret("");
  };
  const handleStartSetup = async () => {
    setError("");
    setLoading(true);
    try {
      const {
        data
      } = await api.mfaSetup();
      setQrCode(data.qrCode);
      setSecret(data.secret);
      setStep(STEP.SCAN);
    } catch (e) {
      setError(e.response?.data?.message || "Failed to start MFA setup");
    } finally {
      setLoading(false);
    }
  };
  const handleVerifySetup = async e => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.mfaVerifySetup(code);
      setMfaEnabled(true);
      setSuccess("Two-factor authentication has been enabled successfully.");
      reset();
    } catch (e) {
      setError(e.response?.data?.message || "Invalid code. Try again.");
    } finally {
      setLoading(false);
    }
  };
  const handleDisable = async e => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.mfaDisable(code);
      setMfaEnabled(false);
      setSuccess("Two-factor authentication has been disabled.");
      reset();
    } catch (e) {
      setError(e.response?.data?.message || "Invalid code. Try again.");
    } finally {
      setLoading(false);
    }
  };
  if (mfaEnabled === null) {
    return <Box sx={{
      display: "flex",
      justifyContent: "center",
      py: 8
    }}>
        <CircularProgress />
      </Box>;
  }
  return <Box>
      {success && <Alert severity="success" sx={{
      mb: 2
    }} onClose={() => setSuccess("")}>
          {success}
        </Alert>}

      <Paper elevation={1} sx={{
      borderRadius: 2,
      overflow: "hidden"
    }}>
        {/* Status banner */}
        <Box sx={{
        p: 3,
        display: "flex",
        alignItems: "center",
        gap: 2,
        bgcolor: mfaEnabled ? "success.50" : "grey.50",
        borderBottom: "1px solid",
        borderColor: mfaEnabled ? "success.200" : "divider"
      }}>
          {mfaEnabled ? <ShieldCheck size={32} color="#2e7d32" /> : <ShieldOff size={32} color="#9e9e9e" />}
          <Box sx={{
          flexGrow: 1
        }}>
            <Typography fontWeight={700}>
              {mfaEnabled ? t("Two-Factor Authentication is ON") : t("Two-Factor Authentication is OFF")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {mfaEnabled ? t("Your account is protected. A code is required on every login.") : t("Enable 2FA to add an extra layer of security to your account.")}
            </Typography>
          </Box>
          <Chip label={mfaEnabled ? "Enabled" : "Disabled"} color={mfaEnabled ? "success" : "default"} size="small" />
        </Box>

        <Box sx={{
        p: 3
      }}>
          {/* IDLE */}
          {step === STEP.IDLE && <>
              {!mfaEnabled ? <Box>
                  <Typography variant="body2" color="text.secondary" sx={{
              mb: 3
            }}>{t("Use an authenticator app like")}<strong>{t("Google Authenticator")}</strong>,{" "}
                    <strong>{t("Authy")}</strong>{t(", or")}<strong>{t("Microsoft Authenticator")}</strong>{t("to generate one-time codes required in addition to your password.")}</Typography>
                  <Button variant="contained" color="success" startIcon={loading ? <CircularProgress size={16} /> : <ShieldCheck size={16} />} onClick={handleStartSetup} disabled={loading}>{t("Enable Two-Factor Authentication")}</Button>
                </Box> : <Box>
                  <Typography variant="body2" color="text.secondary" sx={{
              mb: 3
            }}>{t("To disable two-factor authentication, enter the current 6-digit code from your authenticator app to confirm.")}</Typography>
                  <Button variant="outlined" color="error" startIcon={<ShieldOff size={16} />} onClick={() => {
              setStep(STEP.DISABLE);
              setError("");
            }}>{t("Disable Two-Factor Authentication")}</Button>
                </Box>}
            </>}

          {/* SCAN */}
          {step === STEP.SCAN && <Box>
              <Typography fontWeight={600} sx={{
            mb: 1
          }}>{t("Step 1 — Scan this QR code")}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{
            mb: 2
          }}>{t("Open your authenticator app and scan the QR code below. If you can't scan, enter the key manually.")}</Typography>

              {qrCode && <Box sx={{
            textAlign: "center",
            mb: 2
          }}>
                  <Box component="img" src={qrCode} alt={t("MFA QR Code")} sx={{
              width: 200,
              height: 200,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1
            }} />
                </Box>}

              <Box sx={{
            bgcolor: "grey.50",
            borderRadius: 1,
            p: 1.5,
            mb: 3,
            wordBreak: "break-all",
            fontFamily: "monospace",
            fontSize: "0.8rem",
            color: "text.secondary",
            textAlign: "center"
          }}>
                <Typography variant="caption" display="block" sx={{
              mb: 0.5
            }}>{t("Manual entry key:")}</Typography>
                {secret}
              </Box>

              <Divider sx={{
            my: 2
          }} />

              <Typography fontWeight={600} sx={{
            mb: 1
          }}>{t("Step 2 — Enter the 6-digit code to confirm")}</Typography>
              <Box component="form" onSubmit={handleVerifySetup}>
                {error && <Alert severity="error" sx={{
              mb: 2
            }}>{error}</Alert>}
                <TextField label={t("Authenticator Code")} value={code} onChange={e => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} fullWidth size="small" inputProps={{
              maxLength: 6,
              style: {
                letterSpacing: "0.3em",
                textAlign: "center",
                fontSize: "1.2rem"
              }
            }} placeholder="000000" autoFocus sx={{
              mb: 2
            }} />
                <Box sx={{
              display: "flex",
              gap: 1
            }}>
                  <Button type="submit" variant="contained" color="success" disabled={loading || code.length !== 6} startIcon={loading ? <CircularProgress size={16} /> : <CheckCircle size={16} />}>{t("Confirm & Enable")}</Button>
                  <Button variant="outlined" color="inherit" onClick={reset}>{t("Cancel")}</Button>
                </Box>
              </Box>
            </Box>}

          {/* DISABLE */}
          {step === STEP.DISABLE && <Box component="form" onSubmit={handleDisable}>
              <Box sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 2
          }}>
                <KeyRound size={18} color="#d32f2f" />
                <Typography fontWeight={600} color="error.main">{t("Confirm Disable")}</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{
            mb: 2
          }}>{t("Enter the 6-digit code from your authenticator app to disable 2FA.")}</Typography>
              {error && <Alert severity="error" sx={{
            mb: 2
          }}>{error}</Alert>}
              <TextField label={t("Authenticator Code")} value={code} onChange={e => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} fullWidth size="small" inputProps={{
            maxLength: 6,
            style: {
              letterSpacing: "0.3em",
              textAlign: "center",
              fontSize: "1.2rem"
            }
          }} placeholder="000000" autoFocus sx={{
            mb: 2
          }} />
              <Box sx={{
            display: "flex",
            gap: 1
          }}>
                <Button type="submit" variant="contained" color="error" disabled={loading || code.length !== 6} startIcon={loading ? <CircularProgress size={16} /> : <ShieldOff size={16} />}>{t("Disable 2FA")}</Button>
                <Button variant="outlined" color="inherit" onClick={reset}>{t("Cancel")}</Button>
              </Box>
            </Box>}
        </Box>
      </Paper>
    </Box>;
}

// ── Full page (used by the /settings/mfa route) ───────────────────────────────
export default function MFASettings() {
  const {
    t
  } = useTranslation("admin");
  return <Container maxWidth={false} className="AppContainer-root">
      <TitleBox title={t("Two-Factor Authentication")} subTitle="Secure your account with an authenticator app" allowPrint={false} />
      <MFAContent />
    </Container>;
}
